import React from 'react'
import { isEmpty } from 'utils/object'
import { t } from 'ttag'

import { INTERVIEW_STEPS } from 'ducks/router'
import { STATIC_PAGES } from '../model'

// NOTE: hook that handles state and logic to maintain
// steps transition, switching mode from read only to write,
// etc. and exposes state and handlers for other local hooks
// and the interview components
export const useStepsState = state => {
  const { current_status, evaluation, sections } = state

  const [activeStep, setActiveStep] = React.useState<string | null>(null)

  const { status } = evaluation ?? {}

  const isReadOnly = isEmpty(evaluation) || evaluation == null

  const activeSection = React.useMemo(
    () =>
      sections.find(section => section?.key === activeStep && section) ?? {},
    [activeStep, sections],
  )

  const steps = React.useMemo(() => {
    const summary = [{ key: INTERVIEW_STEPS.SUMMARY, title: t`Summary` }]

    return sections
      .concat(!isReadOnly ? summary : [])
      .map(({ key, scoreable, title }) => {
        const isActive = activeStep === key

        return {
          key,
          label: title,
          scoreable,
          isActive,
        }
      })
  }, [activeStep, sections, isReadOnly])

  const currentStepIndex = React.useMemo(
    () => steps.findIndex(step => step?.key === activeStep),
    [activeStep, steps],
  )

  const isNextStepDisabled = activeStep === steps.at(-1)?.key
  const isPreviousStepDisabled = activeStep === steps[0]?.key

  const isCategoryStep = steps.some(({ key }) => key === activeStep)
  const isIntroductionStep = activeStep === INTERVIEW_STEPS.INTRODUCTION
  const isReportStep = activeStep === INTERVIEW_STEPS.REPORT
  const isSummaryStep = activeStep === INTERVIEW_STEPS.SUMMARY
  const isStaticStep = STATIC_PAGES.includes(activeStep ?? '')

  const handleChangeStep = React.useCallback(
    step => {
      setActiveStep(step)
    },
    [setActiveStep],
  )

  const handleChangeNextStep = React.useCallback(() => {
    if (currentStepIndex >= steps.length - 1) return

    const nextStep = steps[currentStepIndex + 1]?.key

    handleChangeStep(nextStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangePreviousStep = React.useCallback(() => {
    if (currentStepIndex <= 0) return

    const previousStep = steps[currentStepIndex - 1]?.key

    handleChangeStep(previousStep)
  }, [currentStepIndex, steps, handleChangeStep])

  React.useEffect(() => {
    if (steps == null || isEmpty(steps) || activeStep != null) return

    status === 4 || current_status === 5
      ? handleChangeStep(INTERVIEW_STEPS.REPORT)
      : handleChangeStep(INTERVIEW_STEPS.INTRODUCTION)
  }, [activeStep, current_status, status, steps, handleChangeStep])

  return {
    state: {
      activeSection,
      activeStep,
      steps,
      isCategoryStep,
      isIntroductionStep,
      isNextStepDisabled,
      isPreviousStepDisabled,
      isReadOnly,
      isReportStep,
      isSummaryStep,
      isStaticStep,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
    },
  }
}
